import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { apiFetch } from './http';
import { retryAfterSeconds } from './retryAfter';
import type { ClubAccount } from '../auth/clubSession';

// ─────────────────────────────────────────────────────────────────────
// ADDING A PASSKEY — the browser half (#680, #675 slice 2).
//
// Two calls around one ceremony: ask the API for options, hand them to the
// authenticator, post back what it returns.
//
// `@simplewebauthn/browser` DOES THE CONVERSION, AND THAT IS WHY IT IS HERE.
// `navigator.credentials.create()` wants `BufferSource` for the challenge, the
// user handle and every excluded credential id; the API sends them as base64url
// strings, because that is what survives JSON. Hand-rolling that conversion is a
// known trap — the failure is not a type error but a ceremony the authenticator
// refuses, with an error that blames the device. The library's JSON option types
// are the server package's own output shape, so the two cannot drift.
//
// MAJOR VERSIONS ARE MATCHED ON PURPOSE: browser 14 against server 14. The JSON
// shapes are a contract between the two packages, and v13's `startRegistration`
// took a different argument shape — so a mismatched pair fails at the one place
// nothing else checks.
//
// WHOSE ACCOUNT THIS IS NEVER APPEARS HERE. The session decides, server-side.
// There is no account id or address in either request, which is the same rule
// `account.ts` follows for changing a password.
// ─────────────────────────────────────────────────────────────────────

/**
 * What adding a passkey can end as.
 *
 * `unsupported` is its own outcome rather than an error string because the screen
 * genuinely should say something different: a browser without WebAuthn is not a
 * failure the person can retry, and offering them a button that cannot work is
 * worse than not offering one.
 *
 * `cancelled` likewise. Dismissing the authenticator prompt is a CHOICE, and
 * reporting it as a failure — red text, "something went wrong" — tells somebody
 * their device is broken when they simply changed their mind.
 */
export type AddPasskeyOutcome =
  | { status: 'added'; id: string; backedUp: boolean }
  /** This browser cannot do WebAuthn at all. */
  | { status: 'unsupported' }
  /** The person dismissed the prompt, or it timed out. */
  | { status: 'cancelled' }
  /**
   * This authenticator ALREADY holds a passkey for this account (#694 review).
   *
   * Its own outcome rather than a flavour of `cancelled`, because it is the
   * opposite news. `excludeCredentials` is scoped to this identity, so the
   * authenticator refusing on those grounds says something POSITIVE and specific:
   * the thing the person was trying to achieve is already true here.
   *
   * It was `cancelled` first, which the screen renders as «no passkey was added»
   * — factually wrong for the one condition being classified, and wrong in the
   * direction that sends somebody looking for a problem that does not exist.
   */
  | { status: 'already_registered' }
  /** The deployment has no Relying Party configured — no passkey door here. */
  | { status: 'not_configured' }
  /** The session is live but older than the high-impact window (#1122): step up first. */
  | { status: 'reauthentication_required' }
  /** The server refused the attestation, or something else went wrong server-side. */
  | { status: 'refused' }
  | { status: 'unreachable' };

/**
 * Did this 403 ask for a step-up rather than refuse outright? (#1122)
 *
 * Adding or removing a passkey wants a sign-in from the last fifteen minutes.
 * The transport layer already opens the step-up prompt on this body; the
 * caller still needs its own word for it, so the screen can say why nothing
 * happened instead of «try again». Read from a clone where the response offers
 * one, so the body stays readable for anyone after us.
 */
async function askedForStepUp(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  const source = typeof res.clone === 'function' ? res.clone() : res;
  const body = (await source.json().catch(() => null)) as { error?: unknown } | null;
  return body?.error === 'reauthentication_required';
}

/** Does this browser offer WebAuthn at all? */
export function passkeysSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator?.credentials?.create === 'function'
  );
}

/**
 * Run the registration ceremony and store the result.
 *
 * `label` is what the person calls this device in their own account. Optional,
 * and sent as typed — the server trims and bounds it.
 */
export async function addPasskey(label?: string): Promise<AddPasskeyOutcome> {
  if (!passkeysSupported()) return { status: 'unsupported' };

  let optionsRes: Response;
  try {
    optionsRes = await apiFetch('/me/passkeys/options', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (optionsRes.status === 503) return { status: 'not_configured' };
  if (await askedForStepUp(optionsRes)) return { status: 'reauthentication_required' };
  if (!optionsRes.ok) return { status: 'refused' };

  const optionsJSON = (await optionsRes.json().catch(() => null)) as never;
  if (!optionsJSON) return { status: 'refused' };

  let attestation: Awaited<ReturnType<typeof startRegistration>>;
  try {
    attestation = await startRegistration({ optionsJSON });
  } catch (error) {
    // ── A DISMISSED PROMPT IS NOT AN ERROR ─────────────────────────
    //
    // The browser throws for everything: the person pressing cancel, a timeout,
    // an authenticator that already holds this credential (`excludeCredentials`
    // doing its job), and a genuine fault. Only the last is worth alarming
    // somebody about.
    //
    // `NotAllowedError` covers cancel AND timeout, which the spec deliberately
    // makes indistinguishable so a page cannot tell whether a user refused. Those
    // two are the only ones that mean «nothing happened».
    //
    // `InvalidStateError` is NOT one of them, and conflating it was the #694
    // review's finding: it means this authenticator already holds a credential
    // from `excludeCredentials`, which is scoped to this identity — so it says
    // «this device already has a passkey for this account», which is the opposite
    // of «nothing was added».
    const name = (error as { name?: string } | null)?.name;
    if (name === 'InvalidStateError') return { status: 'already_registered' };
    if (name === 'NotAllowedError' || name === 'AbortError') return { status: 'cancelled' };
    return { status: 'refused' };
  }

  let verifyRes: Response;
  try {
    verifyRes = await apiFetch('/me/passkeys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response: attestation, ...(label ? { label } : {}) }),
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (verifyRes.status === 503) return { status: 'not_configured' };
  // The window can lapse between the options and the attestation — a slow
  // ceremony on the fifteenth minute. The same answer as above.
  if (await askedForStepUp(verifyRes)) return { status: 'reauthentication_required' };
  if (!verifyRes.ok) return { status: 'refused' };

  const body = (await verifyRes.json().catch(() => null)) as {
    id?: unknown;
    backedUp?: unknown;
  } | null;
  // A 2xx that carries no credential is a protocol failure, not a success — the
  // same rule `otp.ts` follows after the #686 review.
  if (typeof body?.id !== 'string') return { status: 'refused' };
  return { status: 'added', id: body.id, backedUp: body.backedUp === true };
}

// ─────────────────────────────────────────────────────────────────────
// WHAT PASSKEYS DO I HAVE? — `GET /me/passkeys` (#675 slice 10).
//
// Adding one exists and signing in with one exists, so a person can accumulate
// credentials they cannot see. Until this, the only way to ask «is this device
// already registered?» was to try to add one again and read the answer off a
// refusal.
//
// NO REVOKING. «Does removing a credential need a recently verified session?» is
// one of #675's open questions, and an undecided policy is a reason to leave the
// door shut rather than to guess.
// ─────────────────────────────────────────────────────────────────────

/** One registered credential, as the account screen shows it. */
export interface PasskeySummary {
  /** The ROW's id — what a future revoke would name. Not the authenticator's. */
  id: string;
  /** What the person called this device. Theirs, so it may be anything or null. */
  label: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export type PasskeyListOutcome =
  | { status: 'ok'; passkeys: PasskeySummary[] }
  /**
   * This session cannot have passkeys at all — it did not authenticate locally.
   *
   * Its own outcome rather than an empty list, because «you have none» and «you
   * cannot have any here» are different answers and the first is a lie shaped
   * like data. The screen renders nothing at all for it.
   */
  | { status: 'unavailable' }
  | { status: 'unreachable' };

/** An ISO instant the UI can actually format, or `null`. */
function instant(value: unknown): string | null {
  return typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null;
}

/**
 * One row, or `null` if it does not satisfy the declared contract.
 *
 * `null` means FAIL THE WHOLE LIST — see the caller. A row is not skipped.
 */
function summary(row: unknown): PasskeySummary | null {
  const r = row as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return null;
  if (typeof r.id !== 'string' || r.id === '') return null;

  const createdAt = instant(r.createdAt);
  if (createdAt === null) return null;

  // Optional, but not a dumping ground: present-and-unparseable is corruption,
  // and nulling it would render «Ikke brukt ennå» about a credential that HAS
  // been used. Absent or explicitly null is the ordinary never-used case.
  const lastUsedAt =
    r.lastUsedAt === null || r.lastUsedAt === undefined ? null : instant(r.lastUsedAt);
  if (lastUsedAt === null && r.lastUsedAt !== null && r.lastUsedAt !== undefined) return null;

  if (r.label !== null && r.label !== undefined && typeof r.label !== 'string') return null;
  if (!Array.isArray(r.transports) || r.transports.some((x) => typeof x !== 'string')) return null;

  return {
    id: r.id,
    // A blank label is UNNAMED, not corruption — the field is the person's own
    // and they may clear it. This is the one normalisation, and it is a decision
    // about what the string means rather than a repair of a broken row.
    label: typeof r.label === 'string' && r.label.trim() ? r.label : null,
    transports: r.transports as string[],
    createdAt,
    lastUsedAt,
  };
}

export async function listPasskeys(): Promise<PasskeyListOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/me/passkeys');
  } catch {
    return { status: 'unreachable' };
  }

  // ── THE BODY CLASSIFIES, NOT THE STATUS (#700 review, and #699 before it) ──
  //
  // The server's intentional meaning is specifically `400 unsupported_auth_method`.
  // Reading ANY 400 as «this session cannot have passkeys» would let an unrelated
  // future 400 silently hide the whole account-security section, which is a
  // strong claim to make on the strength of a status code alone.
  if (res.status === 400) {
    const refusal = (await res.json().catch(() => null)) as { error?: unknown } | null;
    return refusal?.error === 'unsupported_auth_method'
      ? { status: 'unavailable' }
      : { status: 'unreachable' };
  }
  if (!res.ok) return { status: 'unreachable' };

  const body = (await res.json().catch(() => null)) as { passkeys?: unknown } | null;
  if (!Array.isArray(body?.passkeys)) return { status: 'unreachable' };

  // ── BROKEN IS NOT EMPTY — FAIL THE WHOLE LIST (#700 review) ──────
  //
  // This used to drop rows it could not read and return the rest. That
  // contradicted the invariant this very client exists to hold: three
  // credentials mangled by a projection or serialization defect became
  // `{ status: 'ok', passkeys: [] }`, and the screen told somebody they had no
  // passkeys. Partial corruption was worse — a shorter list than reality, with
  // nothing to show it had been shortened.
  //
  // This is server-owned ACCOUNT SECURITY data, not a feed where skipping one
  // bad item is harmless. The only thing that may mean «you have none» is a
  // valid, literally empty list.
  const passkeys: PasskeySummary[] = [];
  for (const row of body.passkeys) {
    const one = summary(row);
    if (one === null) return { status: 'unreachable' };
    passkeys.push(one);
  }
  return { status: 'ok', passkeys };
}

/**
 * What removing a passkey can end as.
 *
 * `gone` covers «it was not there» as well as «it was not yours» — the server
 * answers the same for both, and the screen's honest reaction is identical:
 * re-read the list, because what it is showing is out of date.
 */
export type PasskeyRemoveOutcome =
  | { status: 'removed' }
  /** The session is live but older than the high-impact window (#1122): step up first. */
  | { status: 'reauthentication_required' }
  | { status: 'gone' }
  | { status: 'unreachable' };

/** Remove one credential, named by the ROW id the list handed out. */
export async function removePasskey(id: string): Promise<PasskeyRemoveOutcome> {
  let res: Response;
  try {
    res = await apiFetch(`/me/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 404) return { status: 'gone' };
  if (await askedForStepUp(res)) return { status: 'reauthentication_required' };
  // Anything else unexpected is OURS, not a statement about the credential —
  // the rule the #686 review established and the #700 review reinforced.
  if (!res.ok) return { status: 'unreachable' };
  return { status: 'removed' };
}

// ─────────────────────────────────────────────────────────────────────
// SIGNING IN WITH A PASSKEY — the browser half (#680, #675 slice 6).
//
// The other direction, and a DOOR rather than an account change: unauthenticated,
// and the account is whichever one the credential belongs to. No address is sent
// and none is asked for — that is the whole point of a discoverable credential,
// and it is also why the server's challenge belongs to nobody.
// ─────────────────────────────────────────────────────────────────────

/**
 * What signing in with a passkey can end as.
 *
 * ── `cancelled` IS DELIBERATELY BROAD HERE, UNLIKE REGISTRATION ────
 *
 * On registration, `InvalidStateError` told us something specific and earned its
 * own outcome (#694 review). Authentication has no equivalent: when no credential
 * on the device matches this Relying Party, the browser throws `NotAllowedError`
 * — **the same error it throws when the person presses cancel**. The spec makes
 * them indistinguishable on purpose, so that a page cannot learn whether somebody
 * holds a passkey for it.
 *
 * So there is no outcome here that can mean «you have no passkey», and the copy
 * must not claim one. A type that could would be a type somebody eventually
 * renders as a false statement.
 */
export type PasskeySignInOutcome =
  | { status: 'signed-in'; token: string; expiresAt: string | null; account: ClubAccount }
  /** Dismissed, timed out, or no credential for this site — indistinguishable. */
  | { status: 'cancelled' }
  /** The server refused the assertion. */
  | { status: 'refused' }
  /**
   * The credential was GOOD and the account is disabled (#695).
   *
   * Only reachable from the VERIFY response, and only because a valid signed
   * assertion made the API say so — identity is proven, authority is withheld.
   * Nothing before the ceremony may produce this: inferring account state from
   * anything the anonymous options request touched would turn the passkey door
   * into the existence oracle it is built not to be.
   */
  | { status: 'account_disabled' }
  /** The same, for a CLOSED account (#1062) — the person or support closed it. */
  | { status: 'account_closed' }
  | { status: 'not_configured' }
  | { status: 'throttled'; retryAfterSeconds: number }
  | { status: 'unreachable' };

/**
 * `remember` asks for the thirty-day personal session (#1091) — see
 * `verifyAccountCode`. The club portal never passes it.
 */
export async function signInWithPasskey(remember = false): Promise<PasskeySignInOutcome> {
  if (!passkeysSupported()) return { status: 'cancelled' };

  let optionsRes: Response;
  try {
    optionsRes = await apiFetch('/auth/club/passkey/options', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (optionsRes.status === 503) return { status: 'not_configured' };
  if (optionsRes.status === 429)
    return { status: 'throttled', retryAfterSeconds: retryAfterSeconds(optionsRes) };
  if (!optionsRes.ok) return { status: 'unreachable' };

  const optionsJSON = (await optionsRes.json().catch(() => null)) as never;
  if (!optionsJSON) return { status: 'unreachable' };

  let assertion: Awaited<ReturnType<typeof startAuthentication>>;
  try {
    assertion = await startAuthentication({ optionsJSON });
  } catch (error) {
    const name = (error as { name?: string } | null)?.name;
    // See the type: «no credential here» and «cancelled» arrive as one error, so
    // they leave as one outcome.
    if (name === 'NotAllowedError' || name === 'AbortError') return { status: 'cancelled' };
    return { status: 'refused' };
  }

  let verifyRes: Response;
  try {
    verifyRes = await apiFetch('/auth/club/passkey/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response: assertion, remember }),
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (verifyRes.status === 503) return { status: 'not_configured' };
  if (verifyRes.status === 429)
    return { status: 'throttled', retryAfterSeconds: retryAfterSeconds(verifyRes) };
  if (verifyRes.status === 401) return { status: 'refused' };
  if (verifyRes.status === 403) {
    // The ONE place account state may be read, because the server reached it by
    // verifying a real signature. A 403 carrying anything else is still ours.
    const refusal = (await verifyRes.json().catch(() => null)) as { error?: unknown } | null;
    if (refusal?.error === 'account_disabled') return { status: 'account_disabled' };
    if (refusal?.error === 'account_closed') return { status: 'account_closed' };
    return { status: 'unreachable' };
  }
  // Anything else unexpected is OURS, not theirs — `recovery.ts`'s rule, and the
  // one the #686 review had to put back into the OTP client.
  if (!verifyRes.ok) return { status: 'unreachable' };

  const body = (await verifyRes.json().catch(() => null)) as {
    token?: unknown;
    expiresAt?: unknown;
    account?: ClubAccount;
  } | null;
  // A 2xx carrying no session is a protocol failure, never a sign-in.
  if (typeof body?.token !== 'string' || !body.account) return { status: 'unreachable' };
  return {
    status: 'signed-in',
    token: body.token,
    expiresAt: typeof body.expiresAt === 'string' ? body.expiresAt : null,
    account: body.account,
  };
}
